/**
 * AR-4 Edging Sync Rate Algorithm
 * Method ID: b1-edging-sync-calculator
 *
 * Evaluates left-right board edging time-alignment quality for a single
 * turn using cross-correlation with lag analysis and DTW alignment.
 *
 * The turn's left/right edge-angle series are split into fixed-size
 * windows. For each window we compute the best cross-correlation lag
 * (time offset) and a DTW alignment score, then judge whether the
 * window is "synced". The final score blends the synced-window ratio,
 * average correlation, average DTW alignment, and average time offset.
 *
 * 评估左右雪板立刃时间对齐质量：将弯内左右立刃角序列切分为定长窗口，
 * 逐窗口计算互相关最佳滞后（时间偏移）与 DTW 对齐分，判定窗口是否同步，
 * 最终综合同步窗口比例、平均相关性、平均 DTW 对齐与平均时间偏移得分。
 *
 * Deterministic per PO-5.4 (no random numbers).
 * 按 PO-5.4 确定性实现（无随机数）。
 */

import { AR4_PARAMS } from '../constants/algorithm-params';
import type { SkiTickData } from '../types/ski-data';
import { dtw, crossCorrBest } from '../utils/dtw';

// ===== Output Interface =====

/** AR-4 立刃同步率结果 / Edging sync rate result for a single turn */
export interface EdgingSyncResult {
  score: number;                    // 0-100 综合评分
  confidence: number;               // 0.95 / 0.85 / 0.7
  sync_ratio: number;               // 0-1 同步窗口比例
  avg_max_correlation: number;      // 平均最佳互相关（绝对值）
  avg_dtw_alignment: number;        // 平均 DTW 对齐分（0-100）
  avg_time_offset_ms: number;       // 平均时间偏移（毫秒，取绝对值）
  time_offset_score: number;        // 0-100 时间偏移得分
  window_count: number;             // 总窗口数
  synced_window_count: number;      // 同步窗口数
  optimal_lag_samples: number;      // 平均最佳滞后（样本，带方向）
  time_offset_ms: number;           // 平均时间偏移 = optimal_lag / sampleRate * 1000
}

// ===== Derived Constants =====

/**
 * Maximum lag in samples.
 *   maxLagSamples = maxLagMs / 1000 * sampleRate = 100 / 1000 * 100 = 10
 *
 * 最大滞后样本数。
 */
const MAX_LAG_SAMPLES = Math.round(
  (AR4_PARAMS.maxLagMs / 1000) * AR4_PARAMS.sampleRate
);

/**
 * Sync-score threshold on the 0-100 window sync score.
 *   syncThreshold (0.8) → 80
 *
 * 窗口同步分阈值（0-100）。
 */
const SYNC_SCORE_THRESHOLD = AR4_PARAMS.syncThreshold * 100;

// ===== Helper: Windowing =====

/**
 * Split a sequence of the given length into non-overlapping windows.
 * Each window is [start, end) with end exclusive. The last window may
 * be smaller than windowSize when length is not a multiple of it.
 *
 * 将给定长度的序列切分为不重叠窗口。每个窗口为 [start, end)（end 不含），
 * 最后一个窗口可能小于 windowSize。
 *
 * @param length - total sequence length / 序列总长度
 * @param windowSize - samples per window / 每窗口样本数
 * @returns array of { start, end } window ranges / 窗口范围数组
 */
export function splitIntoWindows(
  length: number,
  windowSize: number
): { start: number; end: number }[] {
  const windows: { start: number; end: number }[] = [];
  if (length <= 0 || windowSize <= 0) return windows;

  for (let start = 0; start < length; start += windowSize) {
    const end = Math.min(start + windowSize, length);
    windows.push({ start, end });
  }
  return windows;
}

// ===== Helper: Per-Window Analysis =====

/** 单窗口分析结果 / Per-window analysis result */
export interface WindowAnalysis {
  maxCorrelation: number;        // |best correlation|, 0-1
  optimalLag: number;            // best lag in samples (signed)
  timeOffsetMs: number;          // |optimalLag| / sampleRate * 1000
  dtwAlignmentScore: number;     // DTW alignment score, 0-100
  isSynced: boolean;             // window passes sync criteria
  syncScore: number;             // window sync score, 0-100
}

/**
 * Analyze a single window of left/right edge-angle series.
 *
 * 1. crossCorrBest → best correlation & optimal lag (samples)
 *    maxCorrelation = |bestCorrelation|
 *    timeOffsetMs   = |optimalLag| / sampleRate * 1000
 * 2. dtw → alignmentScore (0-100)
 * 3. syncScore = maxCorrelation * 50 + dtwAlignmentScore * 0.5  (0-100)
 * 4. isSynced = (maxCorrelation >= minCorrelation)
 *              && (timeOffsetMs <= maxLagMs)
 *              && (syncScore >= syncThreshold * 100)
 *
 * 分析单个窗口的左右立刃角序列。
 *
 * @param leftSeries - left edge angle samples / 左立刃角样本
 * @param rightSeries - right edge angle samples / 右立刃角样本
 * @returns WindowAnalysis / 窗口分析结果
 */
export function analyzeWindow(
  leftSeries: number[],
  rightSeries: number[]
): WindowAnalysis {
  const n = Math.min(leftSeries.length, rightSeries.length);

  // Not enough samples to compute a meaningful correlation.
  // 样本不足，无法计算有效相关性。
  if (n < 2) {
    return {
      maxCorrelation: 0,
      optimalLag: 0,
      timeOffsetMs: 0,
      dtwAlignmentScore: 0,
      isSynced: false,
      syncScore: 0,
    };
  }

  // 1. Cross-correlation with lag / 互相关滞后分析
  const { bestCorrelation, bestLag } = crossCorrBest(
    leftSeries,
    rightSeries,
    MAX_LAG_SAMPLES
  );
  const maxCorrelation = Math.abs(bestCorrelation);
  const optimalLag = bestLag;
  const timeOffsetMs =
    (Math.abs(optimalLag) / AR4_PARAMS.sampleRate) * 1000;

  // 2. DTW alignment / DTW 对齐
  const dtwResult = dtw(leftSeries, rightSeries);
  const dtwAlignmentScore = dtwResult.alignmentScore;

  // 3. Window sync score (0-100):
  //    correlation contributes up to 50, DTW alignment up to 50.
  //    窗口同步分（0-100）：相关性贡献上限 50，DTW 对齐贡献上限 50。
  const syncScore = maxCorrelation * 50 + dtwAlignmentScore * 0.5;

  // 4. Sync judgment: gating conditions AND score threshold.
  //    同步判定：门控条件与得分阈值同时满足。
  const meetsCorrelation = maxCorrelation >= AR4_PARAMS.minCorrelation;
  const meetsTimeOffset = timeOffsetMs <= AR4_PARAMS.maxLagMs;
  const meetsScore = syncScore >= SYNC_SCORE_THRESHOLD;
  const isSynced = meetsCorrelation && meetsTimeOffset && meetsScore;

  return {
    maxCorrelation,
    optimalLag,
    timeOffsetMs,
    dtwAlignmentScore,
    isSynced,
    syncScore,
  };
}

// ===== Main Entry Point =====

/**
 * AR-4 Edging Sync Rate: evaluate left-right edging time-alignment
 * quality for a single turn.
 * Method ID: b1-edging-sync-calculator
 *
 * Algorithm steps / 算法步骤:
 * 1. Extract left/right edge-angle series over [startFrameIndex, endFrameIndex].
 *    提取 [startFrameIndex, endFrameIndex] 内左右立刃角序列。
 * 2. Split into non-overlapping windows of windowSize (15) samples.
 *    按 windowSize（15）切分为不重叠窗口。
 * 3. Per window: cross-correlation lag + DTW alignment → sync judgment.
 *    逐窗口：互相关滞后 + DTW 对齐 → 同步判定。
 * 4. Confidence tier by window count (>=10 → 0.95, >=5 → 0.85, else 0.7).
 *    按窗口数确定置信度（>=10 → 0.95，>=5 → 0.85，否则 0.7）。
 * 5. Final score (0-100):
 *      sync_ratio * 0.4 + avg_maxCorrelation * 0.3
 *      + avg_dtw_alignment * 0.2 + time_offset_score * 0.1
 *    where the 0-1 terms (sync_ratio, avg_maxCorrelation) are scaled to
 *    0-100 so the weighted sum spans 0-100.
 *    最终得分（0-100）：同步比例、平均相关性（0-1 项按 100 缩放）与
 *    平均 DTW 对齐、时间偏移得分加权求和。
 *
 * Frame range [startFrameIndex, endFrameIndex] is inclusive on both ends.
 * 帧范围 [startFrameIndex, endFrameIndex] 两端均含。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param startFrameIndex - start frame index (inclusive) / 起始帧索引（含）
 * @param endFrameIndex - end frame index (inclusive) / 结束帧索引（含）
 * @returns EdgingSyncResult / 立刃同步率结果
 */
export function computeEdgingSync(
  frames: SkiTickData[],
  startFrameIndex: number,
  endFrameIndex: number
): EdgingSyncResult {
  // Default zero result with the lowest confidence tier.
  // 默认零结果，置信度取最低档。
  const zeroResult: EdgingSyncResult = {
    score: 0,
    confidence: AR4_PARAMS.confidenceLow,
    sync_ratio: 0,
    avg_max_correlation: 0,
    avg_dtw_alignment: 0,
    avg_time_offset_ms: 0,
    time_offset_score: 0,
    window_count: 0,
    synced_window_count: 0,
    optimal_lag_samples: 0,
    time_offset_ms: 0,
  };

  // Clamp to valid inclusive range / 钳制到有效闭区间
  const lo = Math.max(0, Math.floor(startFrameIndex));
  const hi = Math.min(frames.length - 1, Math.floor(endFrameIndex));
  if (lo > hi || frames.length === 0) return zeroResult;

  // Step 1: extract left/right edge-angle series / 步骤 1：提取左右立刃角序列
  const leftSeries: number[] = [];
  const rightSeries: number[] = [];
  for (let i = lo; i <= hi; i++) {
    leftSeries.push(frames[i].edge_angle_left);
    rightSeries.push(frames[i].edge_angle_right);
  }

  // Step 2: windowing / 步骤 2：切窗
  const windows = splitIntoWindows(leftSeries.length, AR4_PARAMS.windowSize);
  const windowCount = windows.length;
  if (windowCount === 0) return zeroResult;

  // Step 3: per-window analysis / 步骤 3：逐窗口分析
  let syncedCount = 0;
  let sumMaxCorrelation = 0;
  let sumDtwAlignment = 0;
  let sumTimeOffsetMs = 0;
  let sumOptimalLag = 0;

  for (const w of windows) {
    const left = leftSeries.slice(w.start, w.end);
    const right = rightSeries.slice(w.start, w.end);
    const analysis = analyzeWindow(left, right);

    if (analysis.isSynced) syncedCount++;
    sumMaxCorrelation += analysis.maxCorrelation;
    sumDtwAlignment += analysis.dtwAlignmentScore;
    sumTimeOffsetMs += analysis.timeOffsetMs;
    sumOptimalLag += analysis.optimalLag;
  }

  // Averages / 均值
  const syncRatio = syncedCount / windowCount;
  const avgMaxCorrelation = sumMaxCorrelation / windowCount;
  const avgDtwAlignment = sumDtwAlignment / windowCount;
  const avgTimeOffsetMs = sumTimeOffsetMs / windowCount;
  const avgOptimalLag = sumOptimalLag / windowCount;

  // time_offset_ms derived from the averaged (signed) optimal lag.
  // 由平均（带方向）最佳滞后推得的时间偏移。
  const timeOffsetMsFromLag =
    (avgOptimalLag / AR4_PARAMS.sampleRate) * 1000;

  // time_offset_score = max(0, 100 - avg_timeOffsetMs), bounded [0, 100].
  // avg_time_offset_ms uses per-window magnitudes so the score stays >= 0.
  // 时间偏移得分：基于每窗口绝对偏移均值，保证得分落在 [0, 100]。
  const timeOffsetScore = Math.max(0, 100 - avgTimeOffsetMs);

  // Step 4: confidence tier / 步骤 4：置信度分级
  let confidence: number;
  if (windowCount >= 10) {
    confidence = AR4_PARAMS.confidenceHigh; // 0.95
  } else if (windowCount >= 5) {
    confidence = AR4_PARAMS.confidenceMid; // 0.85
  } else {
    confidence = AR4_PARAMS.confidenceLow; // 0.7
  }

  // Step 5: final score (0-100) / 步骤 5：最终得分（0-100）
  // sync_ratio and avg_maxCorrelation are 0-1, scaled by 100 so each
  // weight contributes on the 0-100 scale:
  //   sync_ratio * 40 + avg_maxCorrelation * 30
  //   + avg_dtw_alignment * 0.2 + time_offset_score * 0.1
  // Max = 40 + 30 + 20 + 10 = 100.
  const score =
    syncRatio * 0.4 * 100 +
    avgMaxCorrelation * 0.3 * 100 +
    avgDtwAlignment * 0.2 +
    timeOffsetScore * 0.1;
  const finalScore = Math.max(0, Math.min(100, score));

  return {
    score: finalScore,
    confidence,
    sync_ratio: syncRatio,
    avg_max_correlation: avgMaxCorrelation,
    avg_dtw_alignment: avgDtwAlignment,
    avg_time_offset_ms: avgTimeOffsetMs,
    time_offset_score: timeOffsetScore,
    window_count: windowCount,
    synced_window_count: syncedCount,
    optimal_lag_samples: avgOptimalLag,
    time_offset_ms: timeOffsetMsFromLag,
  };
}
